/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import type { DefaultSettingsTheme } from '@/lib/page-designer/default-settings';
import {
    widenImageUrl,
    widenImageSrcSet,
    DEFAULT_WIDEN_TILE_WIDTHS,
    DEFAULT_WIDEN_FALLBACK_WIDTH,
} from '@/lib/images/widen-image';

/**
 * Default `sizes` hint. PLP grid renders 2 columns on mobile, 3 at `sm`, 4 at `md`+. Callers in
 * other layouts (e.g. a carousel) should pass their own `sizes`.
 */
const DEFAULT_TILE_SIZES = '(min-width: 768px) 25vw, (min-width: 640px) 33vw, 50vw';

export interface ProductTileCardProps {
    /** Product title, rendered as the tile heading. */
    title: string;
    /** Link target — the product detail page. */
    href: string;
    /** PDP Main Image (`pdpMainImage`) — the resting image. A Widen CDN URL. */
    mainImageUrl: string;
    /** PDP Alt Image 1 (`pdpAltImage1`) — cross-fades in on desktop hover/keyboard focus. A Widen CDN URL. */
    altImageUrl?: string;
    /** Eyebrow above the title — the product's brand display name (`brandDisplayName`). Hidden if empty. */
    eyebrow?: string;
    /**
     * Price node, rendered left of the CTA. Callers pass `<TilePrice product={…} />` (it reads the
     * active currency and SFCC pricebook data). Omitted → the CTA sits alone, right-aligned.
     */
    price?: ReactNode;
    /** CTA label. */
    ctaLabel?: string;
    /**
     * Card theme, applied to the text area. Per the design the card **contrasts** its container —
     * a light card in a dark section, a dark card in a light section. Defaults to `dark`, the tone
     * for the light PLP / search / carousel pages.
     */
    theme?: DefaultSettingsTheme;
    /**
     * LCP hint. `true` loads the main image eagerly with high fetch priority (use for the first
     * row, above the fold). Defaults to `false` → lazy, low priority. The alt (hover) image is
     * always lazy/low regardless.
     */
    priority?: boolean;
    /** Responsive `sizes` attribute for the images. Defaults to the PLP grid layout. */
    sizes?: string;
    /** `srcset` width ladder (CSS px) requested from Widen. */
    widths?: readonly number[];
    /** Optional Widen quality (1–100). Omit to use Widen's default. */
    quality?: number;
    /** Fires on tile click (e.g. analytics). The tile still navigates to `href`. */
    onClick?: () => void;
    className?: string;
}

/**
 * Product Tile (UCR-2737, redesigned UCR-2824) — the shared brand product card used by the PLP,
 * search, product carousel, and the Featured Products component. A square product image is locked
 * above a themed text area holding an eyebrow (brand), the product name, and a price + "Shop now"
 * CTA row. On desktop hover — or when the card takes keyboard focus — the main image cross-fades to
 * the alt image and the CTA darkens (touch devices don't hover, and don't match `:focus-visible`).
 *
 * The text area carries its own light/dark theme (`bm-theme-*`), chosen to contrast the container
 * per the design. Images are served from Widen's CDN and sized via URL params
 * (see `@/lib/images/widen-image`): a width-capped `srcset` so the browser fetches one
 * slot-appropriate file instead of the full master. The alt image is always lazy + low priority.
 */
export default function ProductTileCard({
    title,
    href,
    mainImageUrl,
    altImageUrl,
    eyebrow,
    price,
    ctaLabel = 'Shop now',
    theme = 'dark',
    priority = false,
    sizes = DEFAULT_TILE_SIZES,
    widths = DEFAULT_WIDEN_TILE_WIDTHS,
    quality,
    onClick,
    className,
}: ProductTileCardProps): ReactElement {
    const trimmedEyebrow = eyebrow?.trim();
    return (
        <Link
            to={href}
            onClick={onClick}
            aria-label={`Shop ${title} now`}
            data-slot="product-tile-card"
            className={cn(
                // `h-full` + a stretched grid cell → every card in a row is the tallest card's height;
                // with the fixed-height image, that makes the text areas line up (see the text area's flex-1).
                'group flex h-full w-full flex-col overflow-hidden',
                'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring',
                className
            )}>
            {/* Image locked above the text area (square). A neutral placeholder stands in if a product
                has no image (avoids an empty `src`). */}
            <div className="relative aspect-square w-full shrink-0 overflow-hidden">
                {mainImageUrl ? (
                    <img
                        src={widenImageUrl(mainImageUrl, { w: DEFAULT_WIDEN_FALLBACK_WIDTH, q: quality })}
                        srcSet={widenImageSrcSet(mainImageUrl, widths, { q: quality })}
                        sizes={sizes}
                        alt=""
                        aria-hidden="true"
                        data-slot="product-tile-card-main-image"
                        loading={priority ? 'eager' : 'lazy'}
                        fetchPriority={priority ? 'high' : 'low'}
                        className={cn(
                            'absolute inset-0 size-full object-cover transition-opacity duration-300',
                            altImageUrl && 'group-hover:opacity-0 group-focus-visible:opacity-0'
                        )}
                    />
                ) : (
                    <div
                        aria-hidden="true"
                        data-slot="product-tile-card-main-image"
                        className="absolute inset-0 size-full bg-muted"
                    />
                )}
                {/* Alt image — cross-fades in on hover, and on keyboard focus of the card (UCR-2982), so
                    tabbing gets the same treatment as pointing. Always lazy/low. */}
                {altImageUrl && (
                    <img
                        src={widenImageUrl(altImageUrl, { w: DEFAULT_WIDEN_FALLBACK_WIDTH, q: quality })}
                        srcSet={widenImageSrcSet(altImageUrl, widths, { q: quality })}
                        sizes={sizes}
                        alt=""
                        aria-hidden="true"
                        data-slot="product-tile-card-alt-image"
                        loading="lazy"
                        fetchPriority="low"
                        className="absolute inset-0 size-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100"
                    />
                )}
            </div>

            {/* Text area — the card owns its surface (bg + text color), padding 16px mobile / 24px desktop.
                `flex-1` + `justify-between` fills the stretched card height and pins the price/CTA row to the
                bottom, so text areas (and their CTAs) line up across a row. A dark card is Organic Black
                (#1C1603) with organic-white text — the design's card surface, a touch warmer than the section's
                `--bm-ink`; a light card uses the shared light theme. (The dark surface is set explicitly rather
                than via `bm-theme-dark`, whose unlayered `--bm-ink` background would beat a utility override.) */}
            <div
                className={cn(
                    theme === 'dark' ? 'bg-bm-organic-black text-bm-organic-white' : 'bm-theme-light',
                    'flex flex-1 flex-col justify-between gap-4 p-4 md:gap-6 md:p-6'
                )}>
                <div className="flex flex-col items-start gap-2">
                    {trimmedEyebrow && (
                        // Eyebrow is the `brandDisplayName` product-record field, which may contain markup
                        // (rendered as HTML on the PDP too) — render it the same way here.
                        <span
                            data-slot="product-tile-card-eyebrow"
                            className={cn(
                                // 22/28px "ticker-tape" condensed subhead — no exact role token (the DS
                                // subhead tokens are 40/54 and 16/20), so the size is explicit.
                                'inline-block p-0.5 font-condensed text-[22px] font-bold uppercase leading-[0.95] md:text-[28px]',
                                // Subtle band in light theme (the dark card's band matches its own
                                // background, so it reads as no band — same as the design).
                                theme === 'light' && 'bg-bm-white-200'
                            )}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: trimmedEyebrow }}
                        />
                    )}
                    <h3 className="type-h2 w-full break-words">{title}</h3>
                </div>
                <div className="flex items-center justify-between gap-4">
                    {/* Empty span keeps the CTA right-aligned when a product has no price. */}
                    {price ? <div data-slot="product-tile-card-price">{price}</div> : <span aria-hidden="true" />}
                    <span
                        data-slot="product-tile-card-cta"
                        className="flex shrink-0 items-center gap-3 bg-bm-green px-6 py-3 font-condensed text-xl font-bold uppercase leading-[0.85] text-bm-ink transition-colors group-hover:bg-bm-green-600 group-focus-visible:bg-bm-green-600">
                        {ctaLabel}
                        <ArrowRight aria-hidden="true" className="size-5 shrink-0" />
                    </span>
                </div>
            </div>
        </Link>
    );
}
